import { useCallback, useLayoutEffect, useRef, useState } from "react";
import type { GroupImperativeHandle } from "react-resizable-panels";

import type { CollapsiblePanelController } from "./collapsible-panel";

export interface UseAutoCollapseByWidthOptions {
  /** The CollapsiblePanel controller this policy drives. */
  controller: CollapsiblePanelController;
  /** Ref to the PanelGroup, to read the live layout percentages. */
  groupRef: React.RefObject<GroupImperativeHandle | null>;
  /** Sibling panel ids whose width to subtract from the container. */
  reservedPanelIds: string[];
  /** Collapse the target when its available width drops below this. */
  minWidthPx: number;
  /** Panel id to monitor for user-driven collapse while dragging a sibling resize handle. */
  targetPanelId?: string;
  /** Collapse the target when its own rendered width is dragged below this. */
  minTargetPanelWidthPx?: number;
  /** When false, the panel is forced expanded (it is the primary surface). */
  enabled: boolean;
}

/**
 * Auto-collapses a `CollapsiblePanel` when the width left for it (container
 * width minus the reserved sibling panels' live shares) falls below
 * `minWidthPx`. Reads the live `PanelGroup` layout, so it tracks user drags as
 * well as container resizes.
 *
 * When `targetPanelId` and `minTargetPanelWidthPx` are provided, the target
 * also collapses when the user drags that panel itself below the target width.
 * That collapse is treated as user intent and is not immediately auto-expanded
 * just because the overall container is wide enough.
 *
 * Oscillation-safe by construction: this drives a single collapsible panel
 * (collapse-in-place, not a panel-set swap), and collapsing it can only grow
 * the reserved siblings' shares — never shrink them. So the collapsed-state
 * available width is always <= the expanded-state available width at the same
 * container width, and the collapse predicate cannot flip-flop across states.
 * Re-entrancy is also bounded: when our own collapse/expand triggers the
 * group's onLayoutChange, re-evaluating calls collapse()/expand() again, but
 * the panel library treats those as no-ops once already in that state.
 *
 * Returns a `containerRef` (attach to the layout container) and an
 * `onLayoutChange` handler. Wire it to the PanelGroup's `onLayoutChange`
 * (not `onLayoutChanged`) so it re-evaluates live during a drag, not only on
 * pointer release.
 */
export const useAutoCollapseByWidth = ({
  controller,
  groupRef,
  reservedPanelIds,
  minWidthPx,
  targetPanelId,
  minTargetPanelWidthPx,
  enabled,
}: UseAutoCollapseByWidthOptions) => {
  const [containerElement, setContainerElement] = useState<HTMLElement | null>(null);
  const targetCollapsedRef = useRef(false);
  const previousTargetPanelWidthRef = useRef<number | undefined>(undefined);
  const { collapse, expand, isCollapsed } = controller;
  // Join to a primitive so the array can be a stable useCallback dependency
  // (a fresh array identity each render would re-create evaluate every time).
  const reservedKey = reservedPanelIds.join(",");

  const evaluate = useCallback(() => {
    if (!enabled) {
      targetCollapsedRef.current = false;
      previousTargetPanelWidthRef.current = undefined;
      if (isCollapsed) {
        expand();
      }
      return;
    }

    const group = groupRef.current;
    if (!group || !containerElement) {
      return;
    }

    const layout = group.getLayout();

    if (Object.keys(layout).length < 2) {
      previousTargetPanelWidthRef.current = undefined;
      return;
    }

    const reservedPercent = reservedKey
      .split(",")
      .filter(Boolean)
      .reduce((total, id) => total + (layout[id] ?? 0), 0);
    const containerWidth = containerElement.getBoundingClientRect().width;
    const availableWidth = containerWidth * (1 - reservedPercent / 100);
    const targetPanelPercent = targetPanelId ? layout[targetPanelId] : undefined;
    const targetPanelWidth =
      typeof targetPanelPercent === "number" && Number.isFinite(targetPanelPercent)
        ? containerWidth * (targetPanelPercent / 100)
        : undefined;
    const shouldCollapseByAvailableWidth = availableWidth < minWidthPx;
    const shouldCollapseByTargetWidth =
      minTargetPanelWidthPx !== undefined && targetPanelWidth !== undefined && targetPanelWidth < minTargetPanelWidthPx;
    const shouldCollapse = shouldCollapseByAvailableWidth || shouldCollapseByTargetWidth;
    const didDragTargetPanelBelowMin =
      shouldCollapseByTargetWidth &&
      minTargetPanelWidthPx !== undefined &&
      previousTargetPanelWidthRef.current !== undefined &&
      previousTargetPanelWidthRef.current >= minTargetPanelWidthPx;

    if (!isCollapsed && !shouldCollapseByTargetWidth) {
      targetCollapsedRef.current = false;
    }

    if (shouldCollapse && !isCollapsed) {
      if (didDragTargetPanelBelowMin) {
        targetCollapsedRef.current = true;
      }
      collapse();
    } else if (!shouldCollapseByAvailableWidth && isCollapsed && !targetCollapsedRef.current) {
      expand();
    }

    previousTargetPanelWidthRef.current = targetPanelWidth;
  }, [
    collapse,
    containerElement,
    enabled,
    expand,
    groupRef,
    isCollapsed,
    minTargetPanelWidthPx,
    minWidthPx,
    reservedKey,
    targetPanelId,
  ]);

  useLayoutEffect(() => {
    if (!containerElement || typeof ResizeObserver === "undefined") {
      // Still evaluate once so a narrow initial mount collapses pre-paint.
      evaluate();
      return;
    }

    evaluate();
    const observer = new ResizeObserver(evaluate);
    observer.observe(containerElement);
    return () => observer.disconnect();
  }, [containerElement, evaluate]);

  return { containerRef: setContainerElement, onLayoutChange: evaluate } as const;
};
